// src/PCViewer/Sidebar.tsx
import React from 'react';
import { Divider, message, Tooltip } from "antd";
import { useHotkeys } from 'react-hotkeys-hook';
import * as THREE from 'three';
import LoadPointCloudModal from "../LoadPointCloudModal";
import { NeighborValidationPanel } from './NeighborValidationPanel';
import { PointCloudLoadingPanel } from './PointCloudLoadingPanel';
import { analyzeMarkerNeighbors, MarkerNeighborData, NeighborSummary, printNeighborDebug, computeDistanceAndSlope, correctPPrimeForSlope, ensureNeighborhoodLoaded, releaseForceLoadedNodes, hemisphereRadiusScene, isNearVertical } from '../utils/nearbyPoints';
import { NeighborVisualization, SmoothedLabelMode } from '../utils/neighborVisualization';

type MeasurementMode = 'standard' | 'smoothed';
type ModeMenuTool = 'distance_height' | 'height' | 'distance';

// Max number of completed measurements kept at once. Measurements persist across
// photosphere switches, so over a long session they can pile up; when a new one
// pushes past this limit, the oldest measurement that isn't currently selected is
// auto-removed (with a toast). Change this number to tune the cap.
const MAX_MEASUREMENTS = 100;

interface Props {
    viewer: any; // Potree Viewer 实例
    modalRef: React.RefObject<LoadPointCloudModal>;
    activePanorama: boolean;
    activeFreeCam: boolean;
    canEnterFreeCam: boolean;
    activeSatelliteView: boolean;
    toggleFreeCam: () => void;
    takeScreenShot: () => void;
    togglePanorama: () => void;
    toggleSatelliteView: () => void;
    onToolSelect?: (tool: string) => void;
}

interface MeasurementNeighborState {
    summary: MarkerNeighborData;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    neighbors: { start: any[]; end: any[] };
    pPrime: { start: THREE.Vector3 | null; end: THREE.Vector3 | null };
    // Retry timer for *this* measurement's own analysis, so two measurements
    // analyzing concurrently don't cancel each other's retry.
    retryTimeout: number | null;
    // Last computed smoothed value, and the two clicked positions that produced
    // this analysis. Selecting an unmoved measurement reuses the cache instead of
    // re-analyzing (the analysis force-loads the same neighbourhood → same result).
    smoothedResult: { distance: number; height?: number; slopePercent: number } | null;
    analyzed: { start: THREE.Vector3 | null; end: THREE.Vector3 | null } | null;
}

/**
 * Each measurement keeps its own neighbor-analysis cache directly on itself,
 * rather than in one ref shared across the whole Sidebar. With a single shared
 * cache, having more than one Smoothed measurement on screen at once (a normal
 * workflow — extractors leave several up to screenshot) let re-analyzing one
 * measurement silently overwrite another's numbers/labels whenever their async
 * analyses overlapped.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getNeighborState(measurement: any): MeasurementNeighborState {
    if (!measurement.__neighborState) {
        measurement.__neighborState = {
            summary: { start: null, end: null },
            neighbors: { start: [], end: [] },
            pPrime: { start: null, end: null },
            retryTimeout: null,
            smoothedResult: null,
            analyzed: null,
        };
    }
    return measurement.__neighborState;
}

export const Sidebar: React.FC<Props> = ({ viewer, modalRef, activePanorama, activeFreeCam, canEnterFreeCam, activeSatelliteView, toggleFreeCam, takeScreenShot, togglePanorama, toggleSatelliteView, onToolSelect }) => {
    const [activeTool, setActiveTool] = React.useState<string | null>(null);
    const [activeInfo, setActiveInfo] = React.useState<boolean>(false);
    const [showNeighborPanel, setShowNeighborPanel] = React.useState(false);
    const [neighborData, setNeighborData] = React.useState<MarkerNeighborData>({ start: null, end: null });
    const [smoothedMeasure, setSmoothedMeasure] = React.useState<{
        distance: number;
        height: number;
        slopePercent: number;
    } | null>(null);
    const [distanceHeightMode, setDistanceHeightMode] = React.useState<MeasurementMode>('smoothed');
    const [heightMode, setHeightMode] = React.useState<MeasurementMode>('smoothed');
    const [openModeMenu, setOpenModeMenu] = React.useState<ModeMenuTool | null>(null);
    // Which layout the review panel shows: full smoothed panel vs Standard-only.
    const [panelMode, setPanelMode] = React.useState<'smoothed' | 'standard'>('smoothed');
    // Potree name of the measurement the review panel is showing. The panel derives
    // the tool, mode and which Result columns to show from it — so each group keeps
    // its own identity regardless of which tool is currently active.
    const [reviewToolName, setReviewToolName] = React.useState<string>('');

    const visualizationRef = React.useRef<NeighborVisualization | null>(null);
    // Which measurement's analysis is currently shown in the panel — guards
    // against a late-arriving async analysis (see scheduleNeighborAnalyze)
    // overwriting the panel after the user has switched to a different
    // measurement in the meantime.
    const activeMeasurementRef = React.useRef<any>(null);
    const modeMenuRef = React.useRef<HTMLDivElement | null>(null);
    // Removes the dblclick capture handler of the current insertion.
    const pendingUnblockRef = React.useRef<(() => void) | null>(null);
    // A smoothed measurement waiting to auto-copy its value once analysis produces it.
    const autoCopyRef = React.useRef<any>(null);

    // Single-measurement selection: click a completed measurement (marker or line)
    // to select it, then delete just that one with the Remove Measurement button /
    // Backspace. `hasSelection` drives the button's enabled state (needs a render);
    // the refs are read inside imperative event handlers.
    const [hasSelection, setHasSelection] = React.useState(false);
    // Whether any measurement exists in the scene — drives "Remove All"'s enabled
    // state the same way `hasSelection` drives "Remove Selected".
    const [hasMeasurements, setHasMeasurements] = React.useState(false);
    const selectedMeasurementRef = React.useRef<any>(null);
    // Original measurement color, saved so the selection highlight can be undone.
    const selectionStyleRef = React.useRef<{ measurement: any; originalColorHex: number } | null>(null);
    // Mirror of activeTool for the canvas pointer listener (attached once).
    const activeToolRef = React.useRef<string | null>(null);

    const ensureVisualization = () => {
        if (!visualizationRef.current) {
            visualizationRef.current = new NeighborVisualization(viewer);
        }
        return visualizationRef.current;
    };

    // Defaults to the measurement the panel is currently showing.
    const clearScheduledAnalyze = (measurement: any = activeMeasurementRef.current) => {
        if (!measurement) return;
        const state = getNeighborState(measurement);
        if (state.retryTimeout != null) {
            window.clearTimeout(state.retryTimeout);
            state.retryTimeout = null;
        }
    };

    const clearVisualization = () => {
        // Only clear active hemispheres/neighbors — keep labels on completed measurements.
        visualizationRef.current?.clearEphemeral();
        activeMeasurementRef.current = null;
    };

    const clearAllNeighborVisualization = () => {
        visualizationRef.current?.clear();
        activeMeasurementRef.current = null;
    };

    const hideNeighborPanel = () => {
        clearScheduledAnalyze();
        setShowNeighborPanel(false);
        setNeighborData({ start: null, end: null });
        setSmoothedMeasure(null);
        clearVisualization();
    };

    // Selection highlight — reuses Potree's own hover affordance. Potree brightens
    // a marker on hover with `sphere.material.emissive` (its update() never touches
    // emissive) and repaints sphere/edge color from `measurement.color` every frame.
    // So we keep the whole group lit by brightening `measurement.color` (covers
    // spheres AND lines) and holding the marker glow on emissive.
    const SELECTION_MARKER_EMISSIVE = 0x888888; // same value Potree uses for hover
    const SELECTION_COLOR_LIGHTEN = 0.4; // how far the group color is pushed to white

    const applyMarkerGlow = (measurement: any) => {
        for (const sphere of measurement?.spheres ?? []) {
            sphere.material?.emissive?.setHex?.(SELECTION_MARKER_EMISSIVE);
        }
    };

    // Standard measurements draw their own Potree labels (edge / height / slope …).
    // Give them the same selected look as smoothed labels: a thin white border when
    // selected, Potree's default black border otherwise.
    const LABEL_WHITE = { r: 255, g: 255, b: 255, a: 1.0 };
    const LABEL_BLACK = { r: 0, g: 0, b: 0, a: 1.0 };
    const styleMeasurementLabels = (measurement: any, selected: boolean) => {
        if (!measurement) return;
        const labels = [
            ...(measurement.edgeLabels ?? []),
            ...(measurement.coordinateLabels ?? []),
            ...(measurement.angleLabels ?? []),
            ...(measurement.sphereLabels ?? []),
            measurement.heightLabel,
            measurement.slopeLabel,
            measurement.areaLabel,
            measurement.circleRadiusLabel,
        ].filter(Boolean);
        for (const label of labels) {
            if (typeof label.setBorderColor !== 'function') continue;
            label.borderThickness = selected ? 2 : 4;
            label.setBorderColor(selected ? LABEL_WHITE : LABEL_BLACK);
            // All labels transparent so lines (opaque) never cover them; alphaTest
            // keeps the corners rounded. Selected-on-top is handled by moving the
            // group last in the scene (renderOrder/z are ignored — sortObjects=false).
            if (label.material) {
                label.material.alphaTest = 0.5;
                label.material.transparent = true;
                label.material.needsUpdate = true;
            }
        }
    };

    // Potree renders with sortObjects=false, so within a pass draw order = scene
    // traversal order. Measurements live in the MeasuringTool's own scene (which it
    // renders itself); moving the selected one to the end of that scene's children
    // makes its labels draw last — on top of every other group's labels.
    const reorderMeasurementLast = (measurement: any) => {
        const arr = viewer?.measuringTool?.scene?.children;
        if (!Array.isArray(arr)) return;
        const i = arr.indexOf(measurement);
        if (i >= 0 && i !== arr.length - 1) {
            arr.splice(i, 1);
            arr.push(measurement);
        }
    };

    const clearMeasurementSelection = () => {
        const style = selectionStyleRef.current;
        if (style) {
            const measurement = style.measurement;
            try {
                measurement.color?.setHex?.(style.originalColorHex);
                for (const sphere of measurement?.spheres ?? []) {
                    sphere.material?.emissive?.setHex?.(0x000000);
                }
                styleMeasurementLabels(measurement, false);
            } catch (error) {
                console.error('Error clearing measurement selection:', error);
            }
            selectionStyleRef.current = null;
        }
        selectedMeasurementRef.current = null;
        setHasSelection(false);
        // Drop the on-line label highlight.
        visualizationRef.current?.setSelectedMeasurement(null);
    };

    // Just the visual "selected" state (brighten + marker glow + refs). Panel
    // behaviour is layered on by selectMeasurement / the smoothed listeners.
    const applySelectionHighlight = (measurement: any) => {
        if (selectedMeasurementRef.current === measurement) {
            applyMarkerGlow(measurement); // keep it lit; nothing else to re-capture
            return;
        }
        // Restore any previously selected group before lighting this one up.
        clearMeasurementSelection();
        const originalColorHex = measurement.color?.getHex?.() ?? 0xffffff;
        try {
            const bright = new THREE.Color(originalColorHex).lerp(
                new THREE.Color(0xffffff),
                SELECTION_COLOR_LIGHTEN
            );
            measurement.color?.copy?.(bright);
            applyMarkerGlow(measurement);
        } catch (error) {
            console.error('Error selecting measurement:', error);
            return;
        }
        selectionStyleRef.current = { measurement, originalColorHex };
        selectedMeasurementRef.current = measurement;
        setHasSelection(true);
        // Highlight this measurement's on-line labels (white border) — smoothed
        // labels via the visualization, standard labels via Potree's own sprites.
        visualizationRef.current?.setSelectedMeasurement(measurement);
        styleMeasurementLabels(measurement, true);
        // Draw this group's labels last (on top of other groups' labels).
        reorderMeasurementLast(measurement);
    };

    const isSmoothedMeasurement = (measurement: any) =>
        (measurement?.name ?? '').includes('Smoothed');

    // A NeighborSummary carrying only the clicked marker — enough for the panel's
    // Standard row; the Point Quality block is hidden in standard mode so the null
    // metric fields are never read.
    const markerOnlySummary = (markerPoint: any): NeighborSummary =>
        ({
            n: 0,
            inWindow: 0,
            zSpread: null,
            distance: null,
            centroid: null,
            residualSpread: null,
            verticalOffset: null,
            marker: markerPoint,
            neighbors: [],
        } as NeighborSummary);

    // On-line label mode from the measurement name (per measurement, not a global
    // ref): Distance & Slope shows both, Slope/Height shows slope, Distance shows
    // distance. Keeps each group's labels correct no matter the active tool.
    const labelModeFromName = (name: string): SmoothedLabelMode => {
        if (name.includes('DistanceAndSlope')) return 'distance_slope';
        if (name.includes('Height')) return 'slope_only';
        if (name.includes('Distance')) return 'distance_only';
        return 'distance_slope';
    };

    // Auto-copy the completed measurement's value to the clipboard: Slope tool →
    // slope %, Distance / Distance & Slope tools → distance in inches (D&S copies
    // its distance the same way the standalone Distance tool does). The toast names
    // what was copied ("Slope copied" / "Distance copied") — same wording as
    // Potree's own label-copy, so Standard and Smoothed read identically.
    // (Drawing a Standard measurement is copied by Potree's native drop handler, so
    // this is used for Smoothed completions and for tool/mode switches, where the
    // native handler doesn't fire.)
    const copyMeasurementValue = (name: string, distance: number, slopePercent: number) => {
        let text: string | null = null;
        let kind: 'Slope' | 'Distance' | null = null;
        if (name.includes('Height')) {
            text = slopePercent.toFixed(2);
            kind = 'Slope';
        } else if (name.includes('Distance')) {
            const perNative = viewer?.lengthUnit?.code === 'm' ? 39.3701 : 12;
            text = (distance * perNative).toFixed(2);
            kind = 'Distance';
        }
        if (text == null || kind == null) return;
        try {
            navigator.clipboard?.writeText(text);
        } catch (error) {
            console.warn('Clipboard write failed', error);
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const w = window as any;
        if (typeof w.showCopySuccess === 'function') w.showCopySuccess(`${kind} copied: ${text}`);
    };

    // Standard measurement: show the review panel with the Standard row only — no
    // neighbour analysis, no hemispheres, no Point Quality.
    const showStandardPanelFor = (measurement: any) => {
        const endPoint = measurement.points?.[0];
        const startPoint = measurement.points?.[1];
        if (!startPoint?.position || !endPoint?.position) {
            hideNeighborPanel();
            return;
        }
        clearVisualization();
        setSmoothedMeasure(null);
        setNeighborData({
            start: markerOnlySummary(startPoint),
            end: markerOnlySummary(endPoint),
        });
        setReviewToolName(measurement.name ?? '');
        setPanelMode('standard');
        setShowNeighborPanel(true);
        updateStandardVerticalLabel(measurement);
    };

    // Standard measurements draw Potree's native labels, not the smoothed viz — so
    // the "VERTICAL" on-line label is driven here. When the two clicks are near
    // vertical, hide the native distance/slope labels and draw the green
    // VERTICAL + distance label; otherwise restore the native labels. Runs on
    // completion, selection and every re-adjustment (all go through
    // showStandardPanelFor).
    const updateStandardVerticalLabel = (measurement: any) => {
        const endPos = measurement.points?.[0]?.position;
        const startPos = measurement.points?.[1]?.position;
        if (startPos && endPos && isNearVertical(startPos, endPos)) {
            measurement._hideDistanceLabel = true;
            measurement._hideSlopeLabel = true;
            const viz = ensureVisualization();
            // Show the tool's normal label set (slope-only / distance-only / both)
            // with a VERTICAL header; slope voided to "—". drawVerticalLabel reuses the
            // existing label when the points/tool are unchanged (pure selection), and
            // rebuilds only when they change.
            viz.drawVerticalLabel(measurement, startPos, endPos, labelModeFromName(measurement.name ?? ''));
            // Re-assert the current selection onto the label: applySelectionHighlight
            // early-returns when this measurement is already selected (it only re-glows
            // the markers) and would otherwise leave the label without its white border.
            viz.setSelectedMeasurement(selectedMeasurementRef.current);
        } else {
            measurement._hideDistanceLabel = false;
            measurement._hideSlopeLabel = false;
            visualizationRef.current?.removeLabelsFor(measurement);
        }
    };

    // A cached analysis is reusable when the two clicked points haven't moved since
    // it was computed: the analysis force-loads the same neighbourhood and takes the
    // median, so an unmoved measurement re-analyzes to the same P′ — reusing the
    // cache avoids the network + compute + hemisphere redraw on a pure selection.
    const cachedAnalysisValid = (measurement: any): boolean => {
        const state = getNeighborState(measurement);
        if (!state.analyzed) return false;
        // Need a real result (at least one endpoint analyzed).
        if (!state.summary.start && !state.summary.end) return false;
        const endPos = measurement.points?.[0]?.position;
        const startPos = measurement.points?.[1]?.position;
        if (!startPos || !endPos || !state.analyzed.start || !state.analyzed.end) return false;
        const EPS = 1e-8;
        return (
            startPos.distanceToSquared(state.analyzed.start) < EPS &&
            endPos.distanceToSquared(state.analyzed.end) < EPS
        );
    };

    // Re-render the panel + on-line labels from the measurement's cached analysis,
    // without re-running it. labelSig then reuses the existing labels unchanged.
    const renderFromCache = (measurement: any) => {
        const state = getNeighborState(measurement);
        if (activeMeasurementRef.current === measurement) {
            setNeighborData(state.summary);
            setSmoothedMeasure(state.smoothedResult ?? null);
        }
        updateVisualization(
            measurement,
            { start: state.neighbors.start, end: state.neighbors.end },
            { start: state.pPrime.start, end: state.pPrime.end }
        );
    };

    // Open the validation panel for a smoothed measurement. Reuse the cached
    // analysis when the points are unchanged (pure selection); only (re-)analyze
    // when they moved or there is no cache yet.
    const showSmoothedPanelFor = (measurement: any) => {
        setPanelMode('smoothed');
        setReviewToolName(measurement.name ?? '');
        setShowNeighborPanel(true);
        if (activeMeasurementRef.current !== measurement) {
            // Switching which measurement the panel shows — its own analysis
            // cache lives on the measurement itself (see getNeighborState) and
            // does not need clearing; only the currently-displayed React state
            // does, so the previous measurement's numbers don't flash here.
            activeMeasurementRef.current = measurement;
            setNeighborData({ start: null, end: null });
            setSmoothedMeasure(null);
        }
        if (canAnalyzeMeasurement(measurement)) {
            if (cachedAnalysisValid(measurement)) {
                renderFromCache(measurement);
            } else {
                updateMarkersOnly(measurement);
                scheduleNeighborAnalyze(measurement, null);
            }
        }
    };

    // Unified selection entry point (click a marker/line). Smoothed measurements
    // also show their validation panel; standard measurements hide it — so "panel
    // visible" and "selected" mean the same thing for a smoothed group.
    const selectMeasurement = (measurement: any) => {
        applySelectionHighlight(measurement);
        if (isSmoothedMeasurement(measurement)) {
            showSmoothedPanelFor(measurement);
        } else {
            showStandardPanelFor(measurement);
        }
    };

    // Remove only the selected measurement (Remove Measurement / Backspace).
    const handleRemoveSelectedMeasurement = () => {
        const measurement = selectedMeasurementRef.current;
        if (!measurement) return;
        try {
            if ((viewer.scene?.measurements ?? []).includes(measurement)) {
                viewer.scene.removeMeasurement(measurement);
            }
        } catch (error) {
            console.error('Error removing measurement:', error);
        }
        // Drop any custom on-line labels (smoothed / VERTICAL) tied to it.
        visualizationRef.current?.removeLabelsFor(measurement);
        // The panel always shows the selected measurement, so tear it down too.
        hideNeighborPanel();
        clearMeasurementSelection();
    };

    // Keep at most MAX_MEASUREMENTS completed measurements. Called after a new one
    // completes: if over the cap, remove oldest-first (measurements is in creation
    // order) but never the currently-selected group, then toast once. Disposal of
    // the removed group's GPU resources is handled by removeMeasurement + removeLabelsFor.
    const enforceMeasurementCap = () => {
        const all = viewer.scene?.measurements ?? [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const complete = all.filter((m: any) => m._isComplete);
        if (complete.length <= MAX_MEASUREMENTS) return;
        let removed = 0;
        for (const m of complete) {
            if (complete.length - removed <= MAX_MEASUREMENTS) break;
            if (m === selectedMeasurementRef.current) continue; // protect the selected one
            try {
                viewer.scene.removeMeasurement(m);
                visualizationRef.current?.removeLabelsFor(m);
                removed++;
            } catch (error) {
                console.error('Error auto-removing measurement at cap:', error);
            }
        }
        if (removed > 0) {
            message.info(
                `Reached the ${MAX_MEASUREMENTS}-measurement limit — removed the oldest measurement${
                    removed > 1 ? 's' : ''
                }.`
            );
        }
    };

    const canAnalyzeMeasurement = (measurement: any) => {
        const a = measurement.points[0]?.position;
        const b = measurement.points[1]?.position;
        if (!a || !b) return false;
        // Potree clones start→end on first mouseup; skip until they are actually apart.
        return a.distanceTo(b) > 1e-3;
    };

    const updateVisualization = (
        measurement: any,
        neighbors?: { start: any[]; end: any[] },
        pPrimes?: { start: THREE.Vector3 | null; end: THREE.Vector3 | null },
        options?: { replaceLabels?: boolean }
    ) => {
        // Keep Start/End viz aligned with updateNeighborDataFromMeasurement mapping.
        const endPos = measurement.points[0]?.position ?? null;
        const startPos = measurement.points[1]?.position ?? null;
        const neighborState = getNeighborState(measurement);
        if (neighbors) {
            neighborState.neighbors = neighbors;
        }
        if (pPrimes) {
            neighborState.pPrime = pPrimes;
        }
        // Vertical Distance & Slope measurement → show only the Distance label
        // (Standard); slope is meaningless there. Other tools keep their label mode.
        let labelMode = labelModeFromName(measurement.name ?? '');
        if (
            labelMode === 'distance_slope' &&
            startPos &&
            endPos &&
            isNearVertical(startPos, endPos)
        ) {
            labelMode = 'distance_only';
        }
        const viz = ensureVisualization();
        viz.update({
            measurement,
            startPos,
            endPos,
            startNeighbors: neighborState.neighbors.start,
            endNeighbors: neighborState.neighbors.end,
            startPPrime: neighborState.pPrime.start,
            endPPrime: neighborState.pPrime.end,
            replaceLabels: options?.replaceLabels ?? true,
            labelMode,
        });
        activeMeasurementRef.current = measurement;
    };

    /**
     * Re-analyze neighbor validation.
     * @param onlyIndex Potree marker index to refresh; null refreshes both.
     *   After the first full analyze, subsequent drops should pass only the
     *   moved index — otherwise the untouched endpoint is re-queried against
     *   current visibleNodes (often off-screen) and its point count collapses.
     */
    const updateNeighborDataFromMeasurement = (
        measurement: any,
        logToConsole = false,
        onlyIndex: number | null = null
    ): boolean => {
        // Potree 2-marker insertion: after the first click it immediately clones a
        // second marker and keeps dragging that one. Empirically the stable first
        // sphere ends up as points[1] relative to how users perceive Start/End, so
        // map Start ← points[1], End ← points[0].
        const endIndex = 0;
        const startIndex = 1;
        const endPos = measurement.points[endIndex]?.position;
        const startPos = measurement.points[startIndex]?.position;

        const analyzeStart = onlyIndex === null || onlyIndex === startIndex;
        const analyzeEnd = onlyIndex === null || onlyIndex === endIndex;

        // This measurement's own cache — preserve the untouched side so
        // adjusting Start does not rewrite End, and so two measurements being
        // analyzed around the same time never read/write each other's data.
        const state = getNeighborState(measurement);
        const data: MarkerNeighborData = {
            start: state.summary.start,
            end: state.summary.end,
        };
        let startNeighbors = state.neighbors.start;
        let endNeighbors = state.neighbors.end;
        let startPPrime = state.pPrime.start;
        let endPPrime = state.pPrime.end;
        let smoothedResult: { distance: number; height: number; slopePercent: number } | null = null;
        // True once startPPrime/endPPrime hold a valid smoothed P′ pair. When false
        // (an endpoint had no points, or the measurement isn't analyzable) the on-line
        // label falls back to the raw clicks for BOTH ends so it equals Standard.
        let smoothedPair = false;

        if (canAnalyzeMeasurement(measurement)) {
            if (analyzeStart) {
                const startResult = analyzeMarkerNeighbors(
                    viewer,
                    measurement.points[startIndex],
                    endPos
                );
                data.start = startResult.summary;
                startNeighbors = startResult.neighbors;
                startPPrime = data.start?.centroid
                    ? new THREE.Vector3(
                        data.start.centroid.x,
                        data.start.centroid.y,
                        data.start.centroid.z
                    )
                    : null;
            }

            if (analyzeEnd) {
                const endResult = analyzeMarkerNeighbors(
                    viewer,
                    measurement.points[endIndex],
                    startPos
                );
                data.end = endResult.summary;
                endNeighbors = endResult.neighbors;
                endPPrime = data.end?.centroid
                    ? new THREE.Vector3(
                        data.end.centroid.x,
                        data.end.centroid.y,
                        data.end.centroid.z
                    )
                    : null;
            }

            if (data.start?.centroid && data.end?.centroid) {
                // §11: keep each P′ horizontally at the click and project only its
                // height along the slope — distance stops being systematically short.
                const corrected = correctPPrimeForSlope(data.start, data.end);
                if (corrected) {
                    startPPrime = new THREE.Vector3(
                        corrected.start.x,
                        corrected.start.y,
                        corrected.start.z
                    );
                    endPPrime = new THREE.Vector3(
                        corrected.end.x,
                        corrected.end.y,
                        corrected.end.z
                    );
                    smoothedPair = true;
                    // Vertical / height measurement (e.g. a sign or pole): no surface to
                    // smooth → turn Smoothed OFF (panel shows "—"). The on-line label
                    // still uses P′, which correctPPrimeForSlope set to the raw clicks
                    // here, so it reads the Standard distance.
                    if (isNearVertical(data.start.marker.position, data.end.marker.position)) {
                        smoothedResult = null;
                    } else {
                        const sm = computeDistanceAndSlope(corrected.start, corrected.end);
                        smoothedResult = sm;
                        // Auto-copy the smoothed value once, after this first analysis
                        // following completion (Distance / Slope tools only).
                        if (autoCopyRef.current === measurement) {
                            autoCopyRef.current = null;
                            copyMeasurementValue(measurement.name ?? '', sm.distance, sm.slopePercent);
                        }
                    }
                }
            }
        }

        // No valid smoothed P′ pair (an endpoint had no points, the measurement
        // isn't analyzable, etc.). Draw the on-line label from the raw clicks for
        // BOTH ends so it shows the Standard distance — never a stale projected P′
        // left on the untouched side (which made the label disagree with Standard).
        if (!smoothedPair) {
            smoothedResult = null;
            // Vertical → the reported value is just the Standard distance (raw clicks),
            // which needs no neighbourhood, so still show the on-line label even if an
            // endpoint sampled 0 points. Non-vertical with an empty endpoint (Move
            // point) → hide the label (null P′ → neighborVisualization draws nothing).
            const vertical = !!(startPos && endPos && isNearVertical(startPos, endPos));
            if (vertical && startPos && endPos) {
                startPPrime = new THREE.Vector3(startPos.x, startPos.y, startPos.z);
                endPPrime = new THREE.Vector3(endPos.x, endPos.y, endPos.z);
            } else {
                startPPrime = null;
                endPPrime = null;
            }
        }

        // Vertical Slope-only measurement is voided — slope is meaningless and there
        // is no distance to fall back to — so hide its on-line label entirely.
        // (Vertical Distance & Slope still shows the Standard distance label.)
        if (
            startPos &&
            endPos &&
            isNearVertical(startPos, endPos) &&
            labelModeFromName(measurement.name ?? '') === 'slope_only'
        ) {
            startPPrime = null;
            endPPrime = null;
        }

        // Vertical smoothed measurement: Smoothed is off (smoothedResult is null),
        // so the per-analysis auto-copy above never runs. The VERTICAL label shows
        // the Standard distance (except a Slope-only measurement, which shows no
        // label when vertical → nothing to copy). Copy here so completing /
        // adjusting a vertical measurement still (re-)copies.
        if (
            autoCopyRef.current === measurement &&
            startPos &&
            endPos &&
            isNearVertical(startPos, endPos)
        ) {
            autoCopyRef.current = null;
            if (labelModeFromName(measurement.name ?? '') !== 'slope_only') {
                const v = computeDistanceAndSlope(startPos, endPos);
                copyMeasurementValue('Distance', v.distance, v.slopePercent);
            }
        }

        // Persist onto the measurement's own state regardless of whether it's
        // the one currently shown, so switching back to it later shows the
        // latest analysis instead of triggering a fresh one.
        state.summary = data;
        state.neighbors = { start: startNeighbors, end: endNeighbors };
        state.pPrime = { start: startPPrime, end: endPPrime };
        state.smoothedResult = smoothedResult;
        // Remember the exact clicks this analysis was computed from, so a later
        // pure selection (same points) can reuse it without re-analyzing.
        {
            const aEnd = measurement.points?.[0]?.position;
            const aStart = measurement.points?.[1]?.position;
            state.analyzed = {
                start: aStart ? aStart.clone() : null,
                end: aEnd ? aEnd.clone() : null,
            };
        }

        // Only touch the panel's displayed React state if this measurement is
        // still the one being shown — an async analysis for a measurement the
        // user has since switched away from must not clobber what's on screen.
        if (activeMeasurementRef.current === measurement) {
            setNeighborData(data);
            setSmoothedMeasure(smoothedResult);
        }
        updateVisualization(
            measurement,
            { start: startNeighbors, end: endNeighbors },
            { start: startPPrime, end: endPPrime }
        );

        if (logToConsole && canAnalyzeMeasurement(measurement)) {
            // eslint-disable-next-line no-console
            console.group(
                `[NeighborValidation] drop → P′ (analyze ${
                    onlyIndex === null ? 'both' : onlyIndex === startIndex ? 'Start' : 'End'
                })`
            );
            printNeighborDebug('Start', data.start);
            printNeighborDebug('End', data.end);
            // eslint-disable-next-line no-console
            console.groupEnd();
        }

        const found =
            (data.start?.inWindow ?? 0) > 0 ||
            (data.end?.inWindow ?? 0) > 0;
        return found;
    };

    // Never analyze synchronously inside mouseup — that hitch can shove EarthControls.
    // Load denser octree nodes around the sample first so far-away drops are not stuck
    // with coarse visibleNodes LOD, then analyze off the event stack.
    const scheduleNeighborAnalyze = (measurement: any, onlyIndex: number | null = null) => {
        // Cancel any retry still pending for *this* measurement (not whichever
        // one the panel currently shows — that would cancel the wrong retry
        // when two measurements are being analyzed around the same time).
        clearScheduledAnalyze(measurement);
        activeMeasurementRef.current = measurement;

        const endIndex = 0;
        const startIndex = 1;

        const runAnalyze = async (logToConsole: boolean) => {
            if (!canAnalyzeMeasurement(measurement)) return false;

            const endPos = measurement.points[endIndex]?.position;
            const startPos = measurement.points[startIndex]?.position;
            const analyzeStart = onlyIndex === null || onlyIndex === startIndex;
            const analyzeEnd = onlyIndex === null || onlyIndex === endIndex;

            // Nodes force-loaded below; released after the read so they do not
            // accumulate in GPU memory (Potree never LRU-tracks invisible loads).
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let forceLoadedNodes: any[] = [];
            const searchRadius = hemisphereRadiusScene(viewer);
            try {
                // Start and End are independent point-cloud regions — load them
                // concurrently instead of sequentially. Each can take up to the
                // 2.5s ensureNeighborhoodLoaded timeout, so awaiting them one after
                // another doubled the worst-case wait (and the window during which
                // no value is shown) for a fresh 2-point analysis.
                const loads = await Promise.all([
                    analyzeStart && startPos
                        ? ensureNeighborhoodLoaded(viewer, startPos, searchRadius)
                        : Promise.resolve([]),
                    analyzeEnd && endPos
                        ? ensureNeighborhoodLoaded(viewer, endPos, searchRadius)
                        : Promise.resolve([]),
                ]);
                forceLoadedNodes = loads.flat();
            } catch (err) {
                // eslint-disable-next-line no-console
                console.warn('[NeighborValidation] neighborhood load failed', err);
            }

            // Read the points first, THEN release — findHemisphereNeighbors still
            // needs those node buffers during the read below.
            const found = updateNeighborDataFromMeasurement(measurement, logToConsole, onlyIndex);
            releaseForceLoadedNodes(forceLoadedNodes);
            return found;
        };

        // Next frame: past mouseup / control handling
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                void (async () => {
                    const found = await runAnalyze(true);
                    if (found) return;
                    // One deferred retry if LOD/buffers were not ready yet
                    const state = getNeighborState(measurement);
                    state.retryTimeout = window.setTimeout(() => {
                        void runAnalyze(false);
                        state.retryTimeout = null;
                    }, 200);
                })();
            });
        });
    };

    const updateMarkersOnly = (measurement: any) => {
        // While dragging: refresh hemispheres only — keep prior labels/cache until re-analyze.
        setSmoothedMeasure(null);
        updateVisualization(measurement, undefined, undefined, {
            replaceLabels: false,
        });
    };

    const attachSmoothedListeners = (measurement: any, unblockDblClick: () => void) => {
        activeMeasurementRef.current = measurement;

        const isLive = () =>
            (viewer.scene?.measurements ?? []).includes(measurement);

        measurement.addEventListener('marker_moved', () => {
            // Ignore stray events from a measurement that was cancelled / removed.
            if (!isLive()) return;
            updateMarkersOnly(measurement);
        });
        measurement.addEventListener('marker_dropped', (e: any) => {
            // Ignore stray events from a measurement that was cancelled / removed.
            if (!isLive()) return;
            // Interacting with a smoothed measurement (re-)opens its validation panel.
            setShowNeighborPanel(true);
            // Dragging a *different* smoothed measurement's marker directly (not via
            // the click-to-select path): make it the one the panel shows. Its own
            // analysis cache lives on the measurement itself, so it does not need
            // clearing — only the displayed React state does.
            if (activeMeasurementRef.current !== measurement) {
                activeMeasurementRef.current = measurement;
                setNeighborData({ start: null, end: null });
            }
            // Only analyze once the end marker is placed / adjusted (needs AB direction).
            if (canAnalyzeMeasurement(measurement)) {
                updateMarkersOnly(measurement);
                // First completed analyze refreshes both; later drops only the moved marker
                // so the other endpoint keeps its results (avoids visibleNodes LOD wipe).
                const thisState = getNeighborState(measurement);
                const hasBothCached =
                    thisState.summary.start !== null && thisState.summary.end !== null;
                const onlyIndex = hasBothCached ? e.index : null;
                scheduleNeighborAnalyze(measurement, onlyIndex);
            }
            // Interacting with a completed smoothed measurement also selects it, so
            // "panel open" and "selected" (highlighted + deletable) stay in sync.
            // _isComplete is set on the completion mouseup before this drop fires,
            // so this skips the marker drops made while still drawing the line.
            if (measurement._isComplete && canAnalyzeMeasurement(measurement)) {
                applySelectionHighlight(measurement);
                // Auto-copy on completion, and again on every later Start/End
                // adjustment — the value is copied once this measurement's analysis
                // produces the (re-)smoothed number.
                autoCopyRef.current = measurement;
                // Enforce the cap after this one is selected (so it's never the victim).
                enforceMeasurementCap();
            }
            if (e.index === 0) {
                scheduleUnblockDblClick(unblockDblClick);
            }
        });
        measurement.addEventListener('marker_added', () => {
            if (measurement.points.length >= 2) {
                setActiveTool(null);
            }
            // Do not analyze on add — Potree clones end at start position on first mouseup.
        });
    };

    const attachStandardListeners = (measurement: any, unblockDblClick: () => void) => {
        measurement.addEventListener('marker_added', () => {
            setActiveTool(null);
        });

        measurement.addEventListener('marker_dropped', (e: any) => {
            // On completion (or later drags) show the Standard-only Result and select
            // it, mirroring smoothed. While still drawing, keep the panel hidden.
            if (measurement._isComplete && canAnalyzeMeasurement(measurement)) {
                showStandardPanelFor(measurement);
                applySelectionHighlight(measurement);
                // Sidebar owns Standard copy (Potree's native drop-copy is disabled
                // via window.__doppelSidebarOwnsCopy) — its visibility gate goes stale
                // by a frame when a measurement toggles vertical, dropping or doubling
                // the copy. Copy once here per drop: vertical → the Standard distance
                // shown on the VERTICAL label; otherwise the tool's own value.
                const endPos = measurement.points?.[0]?.position;
                const startPos = measurement.points?.[1]?.position;
                if (startPos && endPos) {
                    const v = computeDistanceAndSlope(startPos, endPos);
                    if (isNearVertical(startPos, endPos)) {
                        // Vertical → copy the distance shown on the VERTICAL label.
                        // A vertical Slope-only measurement shows no label/value, so
                        // there is nothing to copy.
                        if (labelModeFromName(measurement.name ?? '') !== 'slope_only') {
                            copyMeasurementValue('Distance', v.distance, v.slopePercent);
                        }
                    } else {
                        copyMeasurementValue(measurement.name ?? '', v.distance, v.slopePercent);
                    }
                }
                // Enforce the cap after this one is selected (so it's never the victim).
                enforceMeasurementCap();
            } else {
                hideNeighborPanel();
            }
            if (e.index === 0) {
                scheduleUnblockDblClick(unblockDblClick);
            }
        });
    };

    // A new `viewer` means a fresh Potree.Viewer for a newly-loaded point cloud
    // (see PointCloudWindow.loadPointCloud) — Sidebar itself is not remounted, so
    // every ref/cache tied to the *previous* viewer has to be torn down here, or
    // it keeps pointing at a disposed viewer/scene. In particular,
    // NeighborVisualization's per-frame label rescale (keyed to the old viewer's
    // 'update' event) silently stops firing, leaving new labels at whatever raw
    // scale they were created with — the giant on-screen numbers bug.
    React.useEffect(() => {
        return () => {
            clearScheduledAnalyze();
            visualizationRef.current?.dispose();
            visualizationRef.current = null;
            activeMeasurementRef.current = null;
            selectedMeasurementRef.current = null;
            selectionStyleRef.current = null;
            autoCopyRef.current = null;
            pendingUnblockRef.current = null;
            setActiveTool(null);
            setOpenModeMenu(null);
            setShowNeighborPanel(false);
            setNeighborData({ start: null, end: null });
            setSmoothedMeasure(null);
            setHasSelection(false);
            setHasMeasurements(false);
        };
    }, [viewer]);

    React.useEffect(() => {
        if (!openModeMenu) return;

        const onPointerDown = (e: MouseEvent) => {
            if (modeMenuRef.current && !modeMenuRef.current.contains(e.target as Node)) {
                setOpenModeMenu(null);
            }
        };

        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [openModeMenu]);

    useHotkeys('f', () => handleLoadPointCloud(), { enabled: true, keyup: true, keydown: false });
    useHotkeys('w', () => handleDistanceMeasurement(), { enabled: true, keyup: true, keydown: false });
    useHotkeys('e', () => handleHeightMeasurement(heightMode), { enabled: true, keyup: true, keydown: false });
    useHotkeys('d', () => handleDistanceAndHeightMeasurement(distanceHeightMode), { enabled: true, keyup: true, keydown: false });
    useHotkeys('x', () => handleResetMeasurements(), { enabled: true, keyup: true, keydown: false });
    useHotkeys(
        'delete',
        (e) => {
            e.preventDefault();
            handleRemoveSelectedMeasurement();
        },
        { enabled: true, keyup: true, keydown: false }
    );
    useHotkeys('c', () => toggleFreeCam(), { enabled: activeFreeCam || canEnterFreeCam, keyup: true, keydown: false });
    useHotkeys('s', () => takeScreenShot(), { enabled: true, keyup: true, keydown: false });
    useHotkeys('a', () => togglePanorama(), { enabled: !activeFreeCam, keyup: true, keydown: false });
    useHotkeys('z', () => toggleSatelliteView(), { enabled: true, keyup: true, keydown: false });
    useHotkeys('escape', () => handleEscapeMeasurement(), { enabled: true, keyup: true, keydown: false });

    // Keep the pointer listener's view of the active tool current without
    // re-attaching it on every tool change.
    React.useEffect(() => {
        activeToolRef.current = activeTool;
    }, [activeTool]);

    // Click a completed measurement (marker or line) to select it; click empty
    // space to deselect. A left press+release with no movement is a click; any
    // drag is an orbit / marker-move and must not select. Selection is off while a
    // measurement tool is active (those clicks place markers).
    React.useEffect(() => {
        const dom = viewer?.renderer?.domElement;
        if (!dom) return;
        let downX = 0;
        let downY = 0;
        let downButton = -1;

        const onPointerDown = (e: PointerEvent) => {
            downX = e.clientX;
            downY = e.clientY;
            downButton = e.button;
        };

        const onPointerUp = (e: PointerEvent) => {
            const wasLeft = downButton === 0 && e.button === 0;
            downButton = -1;
            if (!wasLeft) return;
            if (activeToolRef.current !== null) return;
            const moved = Math.hypot(e.clientX - downX, e.clientY - downY);
            if (moved > 4) return; // drag / orbit, not a click

            const camera = viewer.scene?.getActiveCamera?.();
            const measurements = viewer.scene?.measurements ?? [];
            if (!camera || measurements.length === 0) {
                clearMeasurementSelection();
                return;
            }

            const rect = dom.getBoundingClientRect();
            const ndc = new THREE.Vector2(
                ((e.clientX - rect.left) / rect.width) * 2 - 1,
                -((e.clientY - rect.top) / rect.height) * 2 + 1
            );
            const raycaster = new THREE.Raycaster();
            raycaster.setFromCamera(ndc, camera);
            // Give the thin measurement lines a pickable tube a few inches wide.
            raycaster.params.Line = { threshold: hemisphereRadiusScene(viewer) };

            const objToMeasure = new Map<THREE.Object3D, any>();
            const targets: THREE.Object3D[] = [];
            for (const m of measurements) {
                for (const sphere of m.spheres ?? []) {
                    objToMeasure.set(sphere, m);
                    targets.push(sphere);
                }
                for (const edge of m.edges ?? []) {
                    objToMeasure.set(edge, m);
                    targets.push(edge);
                }
                // Label sprites (Potree edge/height/slope labels + the smoothed P′
                // labels) are all children of the measurement, so clicking a label
                // selects its group too.
                m.traverse((obj: any) => {
                    if (obj.isSprite && obj.visible) {
                        objToMeasure.set(obj, m);
                        targets.push(obj);
                    }
                });
            }

            const hit = raycaster.intersectObjects(targets, false)[0];
            const picked = hit ? objToMeasure.get(hit.object) : null;
            if (picked) {
                selectMeasurement(picked);
            } else if (selectedMeasurementRef.current) {
                // Clicking empty space deselects — and closes the smoothed panel.
                clearMeasurementSelection();
                hideNeighborPanel();
            }
        };

        dom.addEventListener('pointerdown', onPointerDown);
        dom.addEventListener('pointerup', onPointerUp);
        return () => {
            dom.removeEventListener('pointerdown', onPointerDown);
            dom.removeEventListener('pointerup', onPointerUp);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewer]);

    // Hold the marker glow each frame (Potree's hover-leave resets emissive to
    // black), and drop the selection if the measurement is removed by other means.
    // The group color stays lit on its own — update() keeps repainting from the
    // brightened measurement.color we set.
    React.useEffect(() => {
        if (!viewer?.addEventListener) return;
        const onUpdate = () => {
            const measurement = selectedMeasurementRef.current;
            if (!measurement) return;
            if (!(viewer.scene?.measurements ?? []).includes(measurement)) {
                clearMeasurementSelection();
                return;
            }
            applyMarkerGlow(measurement);
        };
        viewer.addEventListener('update', onUpdate);
        return () => viewer.removeEventListener('update', onUpdate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewer]);

    // Track whether any measurement exists, so "Remove All" can disable itself the
    // same way "Remove Selected" does via `hasSelection`.
    React.useEffect(() => {
        const scene = viewer?.scene;
        if (!scene?.addEventListener) return;
        const sync = () => setHasMeasurements((scene.measurements ?? []).length > 0);
        sync();
        scene.addEventListener('measurement_added', sync);
        scene.addEventListener('measurement_removed', sync);
        return () => {
            scene.removeEventListener('measurement_added', sync);
            scene.removeEventListener('measurement_removed', sync);
        };
    }, [viewer]);

    // Distance Measurement — Standard mode (original Potree distance tool)
    const startDistanceStandard = () => {
        hideNeighborPanel();
        try {
            const measurement = viewer.measuringTool.startInsertion({
                showDistances: true,
                maxMarkers: 2,
                name: 'Distance'
            });

            setActiveTool('distance');

            const unblockDblClick = blockDblClickDuringMeasurement();
            attachStandardListeners(measurement, unblockDblClick);
        } catch (error) {
            console.error('Error starting distance measurement:', error);
        }
    };

    // Warn if the user starts a measurement before the point cloud has finished
    // streaming in — a still-loading (sparse) cloud can make any measurement
    // inaccurate. numNodesLoading > 0 means nodes are still in flight, i.e. the
    // loading panel is below 100%.
    const warnIfCloudLoading = () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const Potree = (window as any).Potree;
        if ((Potree?.numNodesLoading ?? 0) > 0) {
            message.warning(
                'Point cloud is still loading — measurements may be inaccurate until it finishes.'
            );
        }
    };

    // Distance (w) is Standard-only — no Smoothed mode.
    const handleDistanceMeasurement = () => {
        if (activeTool === 'distance') {
            // Re-clicking the active tool cancels the current measurement.
            handleEscapeMeasurement();
            return;
        }
        setOpenModeMenu(null);
        clearMeasurementSelection();
        warnIfCloudLoading();
        startDistanceStandard();
    };

    // Distance and Slope — Standard mode (original behavior)
    const startDistanceHeightStandard = () => {
        hideNeighborPanel();
        try {
            const measurement = viewer.measuringTool.startInsertion({
                showDistances: true,
                showHeight: true,
                maxMarkers: 2,
                name: 'DistanceAndSlope'
            });

            setActiveTool('distance_height');
            setDistanceHeightMode('standard');

            const unblockDblClick = blockDblClickDuringMeasurement();
            attachStandardListeners(measurement, unblockDblClick);
        } catch (error) {
            console.error('Error starting distance & slope measurement:', error);
        }
    };

    // Yellow markers + edges (distinct from cyan standard mode).
    // Potree's update() skips color while only 1 marker exists, so paint materials now.
    const paintSmoothedYellow = (measurement: any) => {
        measurement.color.setHex(0xffff00);
        for (const sphere of measurement.spheres || []) {
            sphere.material?.color?.setHex(0xffff00);
        }
        for (const edge of measurement.edges || []) {
            edge.material?.color?.setHex(0xffff00);
        }
    };

    // Slope Measurement — Standard mode (original Potree Height tool)
    const startHeightStandard = () => {
        hideNeighborPanel();
        try {
            const measurement = viewer.measuringTool.startInsertion({
                showDistances: false,
                showHeight: true,
                maxMarkers: 2,
                name: 'Height'
            });

            setActiveTool('height');
            setHeightMode('standard');

            const unblockDblClick = blockDblClickDuringMeasurement();
            attachStandardListeners(measurement, unblockDblClick);
        } catch (error) {
            console.error('Error starting slope measurement:', error);
        }
    };

    // Slope Measurement — Smoothed mode (neighbor validation + P′)
    const startHeightSmoothed = () => {
        try {
            const measurement = viewer.measuringTool.startInsertion({
                showDistances: false,
                // Show the height edge (XY guide, like Standard); hide Potree's own
                // slope label — we draw the P′ slope label ourselves.
                showHeight: true,
                showEdges: true,
                maxMarkers: 2,
                name: 'HeightSmoothed'
            });
            measurement._hideSlopeLabel = true;
            paintSmoothedYellow(measurement);

            setActiveTool('height');
            setHeightMode('smoothed');
            // Slope tool: the on-line label and the Result show slope only.
            setReviewToolName('HeightSmoothed');
            setPanelMode('smoothed');
            setShowNeighborPanel(true);
            setNeighborData({ start: null, end: null });
            setSmoothedMeasure(null);
            clearVisualization();

            const unblockDblClick = blockDblClickDuringMeasurement();
            attachSmoothedListeners(measurement, unblockDblClick);
        } catch (error) {
            console.error('Error starting smoothed slope measurement:', error);
        }
    };

    const handleHeightMeasurement = (mode: MeasurementMode = 'smoothed') => {
        if (activeTool === 'height' && mode === heightMode) {
            // Re-clicking the active tool cancels the current measurement.
            handleEscapeMeasurement();
            return;
        }
        setOpenModeMenu(null);
        clearMeasurementSelection();
        warnIfCloudLoading();
        if (mode === 'smoothed') {
            startHeightSmoothed();
        } else {
            startHeightStandard();
        }
    };

    const handleHeightModeSelect = (mode: MeasurementMode) => {
        setHeightMode(mode);
        setOpenModeMenu(null);
        handleHeightMeasurement(mode);
    };

    // Distance and Slope — Smoothed mode (neighbor validation)
    const startDistanceHeightSmoothed = () => {
        try {
            const measurement = viewer.measuringTool.startInsertion({
                // Keep edges between original drops; hide Potree labels — values come from P′.
                showDistances: false,
                // Show the height edge (XY guide); hide Potree's own slope label.
                showHeight: true,
                showEdges: true,
                maxMarkers: 2,
                name: 'DistanceAndSlopeSmoothed'
            });
            measurement._hideSlopeLabel = true;
            paintSmoothedYellow(measurement);

            setActiveTool('distance_height');
            setDistanceHeightMode('smoothed');
            setReviewToolName('DistanceAndSlopeSmoothed');
            setPanelMode('smoothed');
            setShowNeighborPanel(true);
            setNeighborData({ start: null, end: null });
            setSmoothedMeasure(null);
            clearVisualization();

            const unblockDblClick = blockDblClickDuringMeasurement();
            attachSmoothedListeners(measurement, unblockDblClick);
        } catch (error) {
            console.error('Error starting smoothed distance & slope measurement:', error);
        }
    };

    const handleDistanceAndHeightMeasurement = (mode: MeasurementMode = 'smoothed') => {
        if (activeTool === 'distance_height' && mode === distanceHeightMode) {
            // Re-clicking the active tool cancels the current measurement.
            handleEscapeMeasurement();
            return;
        }
        setOpenModeMenu(null);
        clearMeasurementSelection();
        warnIfCloudLoading();
        if (mode === 'smoothed') {
            startDistanceHeightSmoothed();
        } else {
            startDistanceHeightStandard();
        }
    };

    const handleDistanceHeightModeSelect = (mode: MeasurementMode) => {
        setDistanceHeightMode(mode);
        setOpenModeMenu(null);
        handleDistanceAndHeightMeasurement(mode);
    };

    // --- Measurement Review: switch tool / mode of the selected group ---------
    // The user can retype the selected measurement as a different tool or mode
    // from the Review panel's toolline. The two clicked points are preserved; the
    // group is rebuilt (points/line/label) as the new (tool, mode). Distance is
    // Standard-only, so Distance always forces Standard.
    type ConvertTool = 'distance' | 'height' | 'distance_height';

    // show* flags + Potree name per (tool, mode) — mirrors the startXxx insertion
    // functions above so a rebuilt group behaves identically to a freshly drawn one.
    const measurementConfig = (tool: ConvertTool, mode: MeasurementMode) => {
        const smoothed = mode === 'smoothed';
        if (tool === 'distance') {
            // Distance is Standard-only.
            return {
                showDistances: true,
                showHeight: false,
                showEdges: true,
                name: 'Distance',
                smoothed: false,
            };
        }
        if (tool === 'height') {
            return smoothed
                ? {
                      showDistances: false,
                      showHeight: true,
                      showEdges: true,
                      name: 'HeightSmoothed',
                      smoothed: true,
                  }
                : {
                      showDistances: false,
                      showHeight: true,
                      showEdges: true,
                      name: 'Height',
                      smoothed: false,
                  };
        }
        // distance_height
        return smoothed
            ? {
                  showDistances: false,
                  showHeight: true,
                  showEdges: true,
                  name: 'DistanceAndSlopeSmoothed',
                  smoothed: true,
              }
            : {
                  showDistances: true,
                  showHeight: true,
                  showEdges: true,
                  name: 'DistanceAndSlope',
                  smoothed: false,
              };
    };

    // Build a completed 2-point measurement non-interactively at fixed positions
    // (same recipe as Potree's loadMeasurement: new Measure → set show* → addMarker
    // ×2 → addMeasurement → mark complete).
    const createMeasurementAtPoints = (
        p0: THREE.Vector3,
        p1: THREE.Vector3,
        tool: ConvertTool,
        mode: MeasurementMode
    ) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const Potree = (window as any).Potree;
        const cfg = measurementConfig(tool, mode);
        const measurement = new Potree.Measure();
        measurement.showDistances = cfg.showDistances;
        measurement.showHeight = cfg.showHeight;
        measurement.showEdges = cfg.showEdges;
        measurement.showArea = false;
        measurement.showAngles = false;
        measurement.showCoordinates = false;
        measurement.showCircle = false;
        measurement.showAzimuth = false;
        measurement.closed = false;
        measurement.maxMarkers = 2;
        measurement.name = cfg.name;
        // Preserve start/end index order (points[0] = end, points[1] = start).
        measurement.addMarker(p0.clone());
        measurement.addMarker(p1.clone());
        viewer.scene.addMeasurement(measurement);
        measurement._isComplete = true;
        if (cfg.smoothed) {
            measurement._hideSlopeLabel = true;
            paintSmoothedYellow(measurement);
        }
        return measurement;
    };

    const convertMeasurement = (newTool: ConvertTool, newMode: MeasurementMode) => {
        const src = selectedMeasurementRef.current || activeMeasurementRef.current;
        const p0 = src?.points?.[0]?.position;
        const p1 = src?.points?.[1]?.position;
        if (!src || !p0 || !p1) return;
        // Distance is Standard-only — never switch it into Smoothed.
        const mode: MeasurementMode = newTool === 'distance' ? 'standard' : newMode;
        const kp0 = p0.clone();
        const kp1 = p1.clone();

        // Tear down the old group and any custom (smoothed / VERTICAL) labels it drew
        // — removeMeasurement disposes its GPU resources; removeLabelsFor drops our
        // label entries so none are left pointing at the removed group.
        try {
            if ((viewer.scene?.measurements ?? []).includes(src)) {
                viewer.scene.removeMeasurement(src);
            }
        } catch (error) {
            console.error('Error removing measurement during convert:', error);
        }
        visualizationRef.current?.removeLabelsFor(src);
        clearMeasurementSelection();
        clearVisualization();

        const measurement = createMeasurementAtPoints(kp0, kp1, newTool, mode);

        // Keep the left toolbar's tool highlight + mode flyout in sync.
        setActiveTool(null);
        if (newTool === 'height') setHeightMode(mode);
        if (newTool === 'distance_height') setDistanceHeightMode(mode);

        const noop = () => {};
        if (mode === 'smoothed') {
            attachSmoothedListeners(measurement, noop);
            setReviewToolName(measurement.name);
            setPanelMode('smoothed');
            setShowNeighborPanel(true);
            setNeighborData({ start: null, end: null });
            setSmoothedMeasure(null);
            applySelectionHighlight(measurement);
            if (canAnalyzeMeasurement(measurement)) {
                // Switching to a smoothed group counts as a fresh completion — copy
                // its value once the analysis produces the smoothed number, just like
                // finishing a drawn measurement (autoCopyRef gates the copy in
                // updateNeighborDataFromMeasurement).
                autoCopyRef.current = measurement;
                updateMarkersOnly(measurement);
                scheduleNeighborAnalyze(measurement, null);
            }
        } else {
            attachStandardListeners(measurement, noop);
            applySelectionHighlight(measurement);
            showStandardPanelFor(measurement);
            // Standard groups are normally copied by Potree's native drop handler,
            // which doesn't fire for this programmatic rebuild — so copy explicitly,
            // treating the switch as a completion. Match the completion handler's
            // vertical rule: vertical → copy Distance (a vertical Slope-only group
            // shows no value, so copy nothing); otherwise the tool's own value.
            const sPos = measurement.points?.[1]?.position;
            const ePos = measurement.points?.[0]?.position;
            if (sPos && ePos) {
                const v = computeDistanceAndSlope(sPos, ePos);
                if (isNearVertical(sPos, ePos)) {
                    if (labelModeFromName(measurement.name ?? '') !== 'slope_only') {
                        copyMeasurementValue('Distance', v.distance, v.slopePercent);
                    }
                } else {
                    copyMeasurementValue(measurement.name ?? '', v.distance, v.slopePercent);
                }
            }
        }
    };

    // Remove All
    const handleResetMeasurements = () => {
        try {
            viewer.scene.removeAllMeasurements();
            setActiveTool(null);
            setOpenModeMenu(null);
            clearMeasurementSelection();
            clearScheduledAnalyze();
            setShowNeighborPanel(false);
            setNeighborData({ start: null, end: null });
            setSmoothedMeasure(null);
            clearAllNeighborVisualization();
        } catch (error) {
            console.error('Error removing measurements:', error);
        }
    };

    // Cancel the current measurement (ESC): stop any in-progress insertion, drop a
    // half-drawn measurement so no stray line/point is left, and deselect the tool.
    // Completed measurements stay — use Reset (x) to clear everything.
    const handleEscapeMeasurement = () => {
        // ESC always drops any single-measurement selection first.
        clearMeasurementSelection();
        const measurements = viewer.scene?.measurements ?? [];
        const current = measurements[measurements.length - 1];
        const incomplete = !!current && !current._isComplete;
        // Nothing to do only if there is no active tool, no half-drawn measurement,
        // and the panel is not open for a selected measurement.
        if (activeTool === null && !incomplete && !showNeighborPanel) return;
        try {
            viewer.dispatchEvent({ type: 'cancel_insertions' });
            if (incomplete && current) {
                viewer.scene.removeMeasurement(current);
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error cancelling measurement:', error);
        }
        // Remove the dblclick capture handler of the cancelled insertion.
        pendingUnblockRef.current?.();
        setActiveTool(null);
        setOpenModeMenu(null);
        hideNeighborPanel();
    };

    const handleLoadPointCloud = () => {
        if (modalRef.current) {
            modalRef.current.showModal();
        } else {
            message.error('Error loading point cloud');
        }
    };

    // block the dblclick event during measurement from zooming in or out
    const blockDblClickDuringMeasurement = () => {
        // Drop any block left over from a previous, uncompleted insertion.
        pendingUnblockRef.current?.();

        const dom = viewer?.renderer?.domElement;
        if (!dom) {
            pendingUnblockRef.current = null;
            return () => {};
        }

        const dblHandler = (e: Event) => {
            e.preventDefault();
            e.stopPropagation();
            // TS: Event doesn't have this property, but browser does
            (e as any).stopImmediatePropagation?.();
        };

        // capture phase, block this event as early as possible
        dom.addEventListener('dblclick', dblHandler, true);

        const unblock = () => {
            dom.removeEventListener('dblclick', dblHandler, true);
            if (pendingUnblockRef.current === unblock) {
                pendingUnblockRef.current = null;
            }
        };
        pendingUnblockRef.current = unblock;
        return unblock;
    };

    // schedule the unblock of the dblclick event to prevent zooming in or out
    const scheduleUnblockDblClick = (unblock: () => void) => {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                unblock();
            });
        });
    };

    return (
        <>
            <PointCloudLoadingPanel viewer={viewer} />
            <NeighborValidationPanel
                visible={showNeighborPanel}
                mode={panelMode}
                toolName={reviewToolName}
                onConvert={convertMeasurement}
                start={neighborData.start}
                end={neighborData.end}
                smoothedMeasure={smoothedMeasure}
                lengthUnit={viewer?.lengthUnit?.code === 'm' ? 'm' : 'ft'}
                distanceUnit={
                    viewer?.lengthUnit?.unitspermeter && viewer?.lengthUnitDisplay?.unitspermeter
                        ? {
                            code: viewer.lengthUnitDisplay.code,
                            perNativeUnit:
                                viewer.lengthUnitDisplay.unitspermeter /
                                viewer.lengthUnit.unitspermeter,
                        }
                        : undefined
                }
            />
            <div className="sidebar-container">
                <SidebarButton
                    icon={`${process.env.PUBLIC_URL}/icons/sidebar/file.svg`}
                    tooltip="File Menu (f)"
                    onClick={handleLoadPointCloud}
                    showTooltip={activeInfo}
                />

                <Divider />
                {/* Distance Measurement — Standard only (no Smoothed mode) */}
                <SidebarButton
                    icon={`${process.env.PUBLIC_URL}/icons/sidebar/distance.svg`}
                    tooltip="Distance (w)"
                    active={activeTool === 'distance'}
                    onClick={() => handleDistanceMeasurement()}
                    showTooltip={activeInfo}
                />
                {/* Slope Measurement — Standard / Smoothed */}
                <div
                    className="sidebar-button-with-menu"
                    ref={openModeMenu === 'height' ? modeMenuRef : undefined}
                >
                    <SidebarButton
                        icon={`${process.env.PUBLIC_URL}/icons/sidebar/slope.svg`}
                        tooltip="Slope (e)"
                        active={activeTool === 'height'}
                        mode={heightMode}
                        onClick={() => handleHeightMeasurement(heightMode)}
                        onLongPress={() => setOpenModeMenu((prev) => (prev === 'height' ? null : 'height'))}
                        showTooltip={activeInfo}
                        hideTooltip={openModeMenu === 'height'}
                        hasModes
                        modesOpen={openModeMenu === 'height'}
                    />
                    {openModeMenu === 'height' && (
                        <div className="sidebar-mode-flyout">
                            <Tooltip title="Standard mode" placement="right">
                                <button
                                    type="button"
                                    className={`sidebar-mode-option ${heightMode === 'standard' ? 'active' : ''}`}
                                    onClick={() => handleHeightModeSelect('standard')}
                                    aria-label="Standard mode"
                                >
                                    <span className="sidebar-mode-icon sidebar-mode-icon--standard" />
                                </button>
                            </Tooltip>
                            <Tooltip title="Smoothed mode" placement="right">
                                <button
                                    type="button"
                                    className={`sidebar-mode-option ${heightMode === 'smoothed' ? 'active' : ''}`}
                                    onClick={() => handleHeightModeSelect('smoothed')}
                                    aria-label="Smoothed mode"
                                >
                                    <span className="sidebar-mode-icon sidebar-mode-icon--smoothed" />
                                </button>
                            </Tooltip>
                        </div>
                    )}
                </div>
                {/* Distance and Slope Measurement — Standard / Smoothed */}
                <div
                    className="sidebar-button-with-menu"
                    ref={openModeMenu === 'distance_height' ? modeMenuRef : undefined}
                >
                    <SidebarButton
                        icon={`${process.env.PUBLIC_URL}/icons/sidebar/distance&slope.svg`}
                        tooltip="Distance & Slope (d)"
                        active={activeTool === 'distance_height'}
                        mode={distanceHeightMode}
                        onClick={() => handleDistanceAndHeightMeasurement(distanceHeightMode)}
                        onLongPress={() => setOpenModeMenu((prev) => (prev === 'distance_height' ? null : 'distance_height'))}
                        showTooltip={activeInfo}
                        hideTooltip={openModeMenu === 'distance_height'}
                        hasModes
                        modesOpen={openModeMenu === 'distance_height'}
                    />
                    {openModeMenu === 'distance_height' && (
                        <div className="sidebar-mode-flyout">
                            <Tooltip title="Standard mode" placement="right">
                                <button
                                    type="button"
                                    className={`sidebar-mode-option ${distanceHeightMode === 'standard' ? 'active' : ''}`}
                                    onClick={() => handleDistanceHeightModeSelect('standard')}
                                    aria-label="Standard mode"
                                >
                                    <span className="sidebar-mode-icon sidebar-mode-icon--standard" />
                                </button>
                            </Tooltip>
                            <Tooltip title="Smoothed mode" placement="right">
                                <button
                                    type="button"
                                    className={`sidebar-mode-option ${distanceHeightMode === 'smoothed' ? 'active' : ''}`}
                                    onClick={() => handleDistanceHeightModeSelect('smoothed')}
                                    aria-label="Smoothed mode"
                                >
                                    <span className="sidebar-mode-icon sidebar-mode-icon--smoothed" />
                                </button>
                            </Tooltip>
                        </div>
                    )}
                </div>

                {/* Remove selected measurement (enabled only when one is selected) */}
                <SidebarButton
                    icon={`${process.env.PUBLIC_URL}/icons/sidebar/remove-measurement.svg`}
                    tooltip="Remove Measurement (Delete)"
                    onClick={handleRemoveSelectedMeasurement}
                    disabled={!hasSelection}
                    showTooltip={activeInfo}
                />
                {/* Reset All Measurements */}
                <SidebarButton
                    icon={`${process.env.PUBLIC_URL}/icons/sidebar/reset_tools.svg`}
                    tooltip="Remove All Measurements (x)"
                    onClick={handleResetMeasurements}
                    disabled={!hasMeasurements}
                    showTooltip={activeInfo}
                />
                <Divider />

                <SidebarButton
                    icon={`${process.env.PUBLIC_URL}/icons/sidebar/freecam.svg`}
                    tooltip={activeFreeCam ? 'Exit Free Cam (c)' : 'Enter Free Cam (c)'}
                    active={activeFreeCam}
                    disabled={!activeFreeCam && !canEnterFreeCam}
                    onClick={toggleFreeCam}
                    showTooltip={activeInfo}
                />
                <SidebarButton
                    icon={`${process.env.PUBLIC_URL}/icons/sidebar/camera.svg`}
                    tooltip="Take Screen Shot (s)"
                    onClick={takeScreenShot}
                    showTooltip={activeInfo}
                />

                {/* Background toggle (hidden in Free Cam) */}
                {!activeFreeCam && (
                    <SidebarButton
                        icon={`${process.env.PUBLIC_URL}/icons/sidebar/background-${activePanorama ? 'show' :'hide' }.svg`}
                        tooltip={`${activePanorama ? 'Hide Background Image (a)' : 'Show Background Image (a)'}`}
                        onClick={togglePanorama}
                        showTooltip={activeInfo}
                    />
                )}
                <SidebarButton
                    icon={`${process.env.PUBLIC_URL}/icons/sidebar${activeSatelliteView ? '/default-map-icon.svg' : '/satellite-icon.svg'}`}
                    tooltip={activeSatelliteView ? 'Switch to Default Map (z)' : 'Switch to Satellite View (z)'}
                    onClick={toggleSatelliteView}
                    showTooltip={activeInfo}
                />
                <div className='sidebar-button-bottom'>
                    <SidebarButton
                        icon={`${process.env.PUBLIC_URL}/icons/sidebar/info-${activeInfo ? 'hide' : 'show'}.svg`}
                        tooltip={`${activeInfo ? 'Hide All Tooltips' : 'Show All Tooltips'}`}
                        onClick={() => setActiveInfo(!activeInfo)}
                    />
                </div>

            </div>
        </>
    );
}

// Hold the button this long before a press counts as "long press" instead of a click.
const LONG_PRESS_MS = 1000;

// SidebarButton component
interface ButtonProps {
    icon: string;
    tooltip: string;
    active?: boolean;
    disabled?: boolean;
    onClick: () => void;
    // Fires once the button has been held for LONG_PRESS_MS; the click that
    // follows on release is swallowed so it doesn't also toggle the tool.
    onLongPress?: () => void;
    showTooltip?: boolean;
    hideTooltip?: boolean;
    hasModes?: boolean;
    modesOpen?: boolean;
    // Current measurement mode for tools that have one; drives the yellow
    // (smoothed) / blue (standard) accent on the arrow and active button.
    mode?: MeasurementMode;
}

const SidebarButton: React.FC<ButtonProps> = ({
    icon,
    tooltip,
    active,
    disabled,
    onClick,
    onLongPress,
    showTooltip,
    hideTooltip,
    hasModes,
    modesOpen,
    mode,
}) => {
    const longPressTimerRef = React.useRef<number | null>(null);
    const longPressFiredRef = React.useRef(false);

    const clearLongPressTimer = () => {
        if (longPressTimerRef.current !== null) {
            window.clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
    };

    const startLongPress = () => {
        if (!onLongPress) return;
        longPressFiredRef.current = false;
        clearLongPressTimer();
        longPressTimerRef.current = window.setTimeout(() => {
            longPressFiredRef.current = true;
            longPressTimerRef.current = null;
            onLongPress();
        }, LONG_PRESS_MS);
    };

    React.useEffect(() => clearLongPressTimer, []);

    const handleClick = () => {
        // The long press already fired its own action — don't also toggle the tool.
        if (longPressFiredRef.current) {
            longPressFiredRef.current = false;
            return;
        }
        onClick();
    };

    const tooltipContent = hasModes ? (
        <>
            <div>{tooltip}</div>
            <div className="sidebar-tooltip-hint">Long press to switch mode</div>
        </>
    ) : (
        tooltip
    );

    return (
        <Tooltip
            key={showTooltip && !hideTooltip ? 'visible' : 'hover'}
            title={tooltipContent}
            placement="right"
            {...(hideTooltip ? { visible: false } : showTooltip ? { visible: true } : {})}
        >
            <div
                className={[
                    'sidebar-button',
                    active ? 'active' : '',
                    disabled ? 'disabled' : '',
                    hasModes ? 'has-modes' : '',
                    modesOpen ? 'modes-open' : '',
                    mode ? `mode-${mode}` : '',
                ].filter(Boolean).join(' ')}
                onClick={disabled ? undefined : handleClick}
                onMouseDown={disabled ? undefined : startLongPress}
                onMouseUp={disabled ? undefined : clearLongPressTimer}
                onMouseLeave={disabled ? undefined : clearLongPressTimer}
                onTouchStart={disabled ? undefined : startLongPress}
                onTouchEnd={disabled ? undefined : clearLongPressTimer}
                onTouchCancel={disabled ? undefined : clearLongPressTimer}
                style={disabled ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
            >
                <img src={icon} alt={tooltip} />
            </div>
        </Tooltip>
    );
};
